// RÉPLICA DE PARIDAD admin↔web (regla PointsHelper): cambiar este módulo exige
// cambiar su gemelo en krispis-web (src/api/Orders/ShortId.ts).
//
// Folio de orden: {numSucursal(3)}{ddMMyy}-{seq(5)}. Hasta 2026-08 el seq era
// "conteo de órdenes del día + 1" calculado en cliente: dos dispositivos
// creando orden al mismo tiempo (dos POS, o POS + web) contaban lo mismo y
// generaban el MISMO folio. Ahora el seq se RESERVA de forma atómica con una
// transacción sobre `counters/{scope}_{branchOfficeId}_{ddMMyy}` — la ÚNICA
// transacción del ecosistema (excepción deliberada a la regla "cero
// transacciones Firestore": es un contador dedicado, nunca un doc de negocio).
//
// El contador se protege además con el folio máximo ya existente
// (`Math.max(contador, seedSeq) + 1`): si un dispositivo con build viejo sigue
// escribiendo folios por conteo, los nuevos siempre quedan por encima. Si la
// transacción falla (sin conexión, etc.) se cae al comportamiento viejo
// (seed + 1) — la venta NUNCA se bloquea por el folio.
import { collection, doc, getDocs, limit, orderBy, query, runTransaction, where } from 'firebase/firestore';
import { firestore } from '@src/api/Firebase/Firebase';
import { format } from 'date-fns';

export const COUNTERS_COLLECTION = 'counters';
export const countersRef = collection(firestore, COUNTERS_COLLECTION);

// 'orders' = folios de venta (POS/web) · 'inventory-orders' = pedidos de insumos
export type ShortIdScope = 'orders' | 'inventory-orders';

export const getShortIdDayKey = (date: Date): string => format(date, 'ddMMyy');

export const buildShortId = (branchNumber: string | number | undefined, date: Date, seq: number): string =>
    `${String(Number(branchNumber || '1')).padStart(3, '0')}${getShortIdDayKey(date)}-${String(seq).padStart(5, '0')}`;

// Máximo seq entre folios existentes; ignora folios malformados. 0 si no hay.
export const getMaxShortIdSeq = (shortIds: Array<string | undefined>): number =>
    shortIds.reduce((max: number, shortId) => {
        const seq = Number((shortId || '').split('-')[1]);
        return Number.isFinite(seq) && seq > max ? seq : max;
    }, 0);

export const getNextShortIdSeq = async (
    scope: ShortIdScope,
    branchOfficeId: string,
    date: Date,
    seedSeq: number
): Promise<number> => {
    const counterRef = doc(countersRef, `${scope}_${branchOfficeId}_${getShortIdDayKey(date)}`);
    try {
        return await runTransaction(firestore, async (transaction) => {
            const snapshot = await transaction.get(counterRef);
            const current = snapshot.exists() ? Number(snapshot.data()?.seq) || 0 : 0;
            const next = Math.max(current, seedSeq) + 1;
            transaction.set(counterRef, { scope, branchOfficeId, seq: next, lastUpdate: new Date() });
            return next;
        });
    } catch (error) {
        console.error('No se pudo reservar el folio en el contador; fallback al conteo local', error);
        return seedSeq + 1;
    }
};

export const generateShortId = async (options: {
    scope: ShortIdScope;
    branchOfficeId: string;
    branchNumber: string | number | undefined;
    date: Date;
    existingShortIds: Array<string | undefined>;
}): Promise<string> => {
    const seq = await getNextShortIdSeq(
        options.scope,
        options.branchOfficeId,
        options.date,
        getMaxShortIdSeq(options.existingShortIds)
    );
    return buildShortId(options.branchNumber, options.date, seq);
};

/** Colección donde viven los folios de cada scope. */
const SHORT_ID_COLLECTIONS: Record<ShortIdScope, string> = {
    orders: 'orders',
    'inventory-orders': 'inventory-orders'
};

/** `{sucursal(3)}{ddMMyy}-`: todo folio de esa sucursal y ese día empieza así. */
export const getShortIdPrefix = (branchNumber: string | number | undefined, date: Date): string =>
    `${String(Number(branchNumber || '1')).padStart(3, '0')}${getShortIdDayKey(date)}-`;

/**
 * Folio máximo YA existente para esa sucursal y ese día, con UNA lectura: rango
 * por prefijo + `orderBy shortId desc` + `limit 1` (mismo campo en el rango y
 * en el orden → sin índice compuesto).
 *
 * Antes cada venta leía TODAS las órdenes del día de la sucursal para sacar este
 * número (auditoría 2026-09-06: cada lectura se paga y el costo crecía con el
 * día). La semántica es la misma: semilla y guardia contra dispositivos que
 * sigan contando por su cuenta. Si la query falla, 0 — el contador manda.
 */
export const getMaxExistingShortIdSeq = async (
    scope: ShortIdScope,
    branchNumber: string | number | undefined,
    date: Date
): Promise<number> => {
    const prefix = getShortIdPrefix(branchNumber, date);
    try {
        const snapshot = await getDocs(
            query(
                collection(firestore, SHORT_ID_COLLECTIONS[scope]),
                where('shortId', '>=', prefix),
                where('shortId', '<', `${prefix}\uf8ff`),
                orderBy('shortId', 'desc'),
                limit(1)
            )
        );
        return getMaxShortIdSeq(snapshot.docs.map((document) => document.data().shortId as string | undefined));
    } catch (error) {
        console.error('No se pudo leer el folio máximo del día; el contador manda', error);
        return 0;
    }
};

/**
 * Reserva un folio completo con DOS lecturas (folio máximo + contador), sin que
 * el llamador tenga que cargar las órdenes del día. Es lo que usan los 4 sitios
 * que generan folio (POS, POS de inventario, checkout web y mesa QR).
 */
export const reserveShortId = async (options: {
    scope: ShortIdScope;
    branchOfficeId: string;
    branchNumber: string | number | undefined;
    date: Date;
}): Promise<string> => {
    const seed = await getMaxExistingShortIdSeq(options.scope, options.branchNumber, options.date);
    const seq = await getNextShortIdSeq(options.scope, options.branchOfficeId, options.date, seed);
    return buildShortId(options.branchNumber, options.date, seq);
};
